import React, { useState } from 'react';
import { EyeOutlined, MedicineBoxOutlined, LoadingOutlined, CheckCircleFilled, CloseOutlined, ClockCircleOutlined, ExclamationCircleOutlined, WarningOutlined } from '@ant-design/icons';
import { useDispenseUnits} from '../../../../hooks/useDispenseUnits'
import {
  Card,
  Header,
  HeaderContent,
  InfoWrapper,
  Avatar,
  AvatarText,
  Details,
  Name,
  Age,
  DOB,
  MetadataWrapper,
  Badge,
  BadgeDot,
  UpdateInfo,
  Location,
  Body,
  BodyInner,
  CEVPrimary,
  CEVHeader,
  CEVTitle,
  CEVStatusBadge,
  CEVFlags,
  CEVFlagsHeader,
  CEVFlagsLabel,
  CEVToggle,
  CEVFlagsContent,
  CEVFlagsText,
  CEVFlaggedQuestions,
  FlaggedQuestionItem,
  FlaggedQuestionHeader,
  FlaggedQuestionIcon,
  FlaggedQuestionText,
  FlaggedQuestionAnswer,
  FlaggedQuestionReason,
  MedContextGrid,
  MedContextCard,
  MedContextLabel,
  MedContextTitle,
  MedContextSub,
  MedContextNote,
  SnapshotCard,
  SnapshotTitle,
  SnapshotGrid,
  SnapshotItem,
  SnapshotLabel,
  SnapshotValue,
  Footer,
  FooterMeta,
  VerifiedBadge,
  StatusText,
  Buttons,
  SecondaryButton,
  PrimaryButton,
  CollapsedSuccess,
  SuccessLeft,
  SuccessIcon,
  SuccessInfo,
  SuccessPatientName,
  SuccessMessage,
  SuccessTimestamp,
  SuccessPrescription,
  SuccessMedication,
  SuccessRight,
  SuccessButton,
  DismissButton,
  CircularProgress,
  CircularProgressSvg,
  CircularProgressBackground,
  CircularProgressForeground,
  CircularProgressIcon,
  FeedbackSection,
  FeedbackLabel,
  FeedbackTextarea,
  OverridePrescribeButton,
  ErrorMessage,
  ErrorDismissButton,
  RenewalBadge,
} from './styles';

const CaseCard = ({
  patient,
  eligibility,
  medicalInfo,
  requestType,
  onViewCase,
  onPrescribe,
  onOverridePrescribe,
  onDismiss,
  onErrorDismiss,
  isSubmitting,
  isDisabled,
  isOverrideDisabled,
  hasError,
  isAIFlagged,
  isFlagged,
  eligibilityGuardTriggered,
  isSuccessCollapsed,
  autoDismissProgress = 0,
}) => {
  const isRenewal = requestType === 'rx renewal';
  const [isExpanded, setIsExpanded] = useState(false);
  const [overrideFeedback, setOverrideFeedback] = useState('');
  const { getDispenseUnitById } = useDispenseUnits();
  const toggleExpand = () => {
    setIsExpanded(!isExpanded);
  };

  const dispenseUnit = getDispenseUnitById(medicalInfo.queuedPrescription?.medication?.dispenseUnitId);

  // Render collapsed success state
  if (isSuccessCollapsed) {
    // Drug name and strength from template, other details from requested medication
    const template = medicalInfo?.queuedPrescription;
    const medication = template?.medication;
    const requested = medicalInfo?.requestedMedication;
    
    const medicationName = medication?.drugName 
      ? `${medication.drugName}${medication.strength ? ` ${medication.strength}` : ''}`
      : 'Medication';
    
    // Build prescription details string
    const prescriptionDetails = [];
    if (requested?.pillCount) {
      const units = requested?.quantityUnits || 'units';
      prescriptionDetails.push(`Qty: ${requested.pillCount} ${units}`);
    }
    if (requested?.refills !== undefined) {
      prescriptionDetails.push(`${requested.refills} refill${requested.refills !== 1 ? 's' : ''}`);
    }
    if (requested?.daysSupply) {
      prescriptionDetails.push(`${requested.daysSupply}-day supply`);
    }
    
    const detailsText = prescriptionDetails.join(' · ');

    return (
      <CollapsedSuccess>
        <SuccessLeft>
          <SuccessIcon>
            <CheckCircleFilled />
          </SuccessIcon>
          <SuccessInfo>
            <SuccessPatientName>{patient.name}</SuccessPatientName>
            <SuccessMessage>
              <span>Prescription approved and sent</span>
              <SuccessTimestamp>• just now</SuccessTimestamp>
            </SuccessMessage>
            <SuccessPrescription>
              <MedicineBoxOutlined />
              <div>
                <SuccessMedication>{medicationName}</SuccessMedication>
                {detailsText && <span> · {detailsText}</span>}
              </div>
            </SuccessPrescription>
          </SuccessInfo>
        </SuccessLeft>
        <SuccessRight>
          <CircularProgress>
            <CircularProgressSvg>
              <CircularProgressBackground
                cx="20"
                cy="20"
                r="18"
              />
              <CircularProgressForeground
                cx="20"
                cy="20"
                r="18"
                $progress={autoDismissProgress}
              />
            </CircularProgressSvg>
            <CircularProgressIcon>
              <ClockCircleOutlined />
            </CircularProgressIcon>
          </CircularProgress>
          <SuccessButton onClick={onViewCase}>
            <EyeOutlined />
            Review Full Chart
          </SuccessButton>
          {onDismiss && (
            <DismissButton onClick={onDismiss} title="Dismiss">
              <CloseOutlined />
            </DismissButton>
          )}
        </SuccessRight>
      </CollapsedSuccess>
    );
  }

  // Render full card view
  return (
    <Card>
      <Header $haserror={isFlagged.toString()}>
        <HeaderContent>
          <InfoWrapper>
            <Avatar>
              <AvatarText>{patient.initials}</AvatarText>
            </Avatar>
            <Details>
              <Name>
                {patient.name}
                {isRenewal && <RenewalBadge>Renewal</RenewalBadge>}
                <Age> · {patient.age} yrs</Age>
              </Name>
              <DOB>DOB {patient.dob}</DOB>
            </Details>
          </InfoWrapper>

          <MetadataWrapper>
            <Badge>
              <BadgeDot $eligibilityGuardTriggered={(eligibilityGuardTriggered || isAIFlagged).toString()} />
              System verdict: {(eligibilityGuardTriggered || isAIFlagged) ? 'Ineligible' : 'Eligible'}
            </Badge>

            <UpdateInfo>
              Updated {patient.lastUpdated} · <Location>{patient.location}</Location>
            </UpdateInfo>
          </MetadataWrapper>
        </HeaderContent>
      </Header>

      <Body>
        <BodyInner>
          <CEVPrimary $haserror={isFlagged.toString()}>
            <CEVHeader>
              <CEVTitle $haserror={isFlagged.toString()}>
                Clinical Eligibility Validator
              </CEVTitle>

              <CEVStatusBadge $haserror={isFlagged.toString()}>
                {Array.isArray(eligibility.flaggedQuestions) && eligibility.flaggedQuestions.length > 0
                  ? `${eligibility.flaggedQuestions.length} flag${eligibility.flaggedQuestions.length !== 1 ? 's' : ''}`
                  : 'No flags'}
              </CEVStatusBadge>
            </CEVHeader>

            <CEVFlags $haserror={isFlagged.toString()}>
              <CEVFlagsHeader>
                <CEVFlagsLabel>AI Analysis</CEVFlagsLabel>

                <CEVToggle
                  type="button"
                  onClick={toggleExpand}
                  aria-expanded={isExpanded}
                  $haserror={isFlagged.toString()}
                >
                  {isExpanded ? 'Collapse' : 'Expand'}
                </CEVToggle>
              </CEVFlagsHeader>

              <CEVFlagsContent $expanded={isExpanded}>
                <CEVFlagsText>{eligibility.description}</CEVFlagsText>
              </CEVFlagsContent>
            </CEVFlags>

            {Array.isArray(eligibility.flaggedQuestions) && eligibility.flaggedQuestions.length > 0 && (
              <CEVFlaggedQuestions>
                {eligibility.flaggedQuestions.map((flaggedItem, index) => (
                  <FlaggedQuestionItem key={index}>
                    <FlaggedQuestionHeader>
                      <FlaggedQuestionIcon>
                        <WarningOutlined />
                      </FlaggedQuestionIcon>
                      <FlaggedQuestionText>{flaggedItem.question}</FlaggedQuestionText>
                    </FlaggedQuestionHeader>
                    <FlaggedQuestionAnswer>
                      <strong>Answer:</strong> {flaggedItem.answer}
                    </FlaggedQuestionAnswer>
                    {(flaggedItem.flag_basis || flaggedItem.reason) && (
                      <FlaggedQuestionReason>
                        {flaggedItem.flag_basis || flaggedItem.reason}
                      </FlaggedQuestionReason>
                    )}
                  </FlaggedQuestionItem>
                ))}
              </CEVFlaggedQuestions>
            )}
          </CEVPrimary>

          <MedContextGrid>
            <MedContextCard $haserror={isFlagged.toString()}>
              <MedContextLabel>Requested medication</MedContextLabel>
              <MedContextTitle>{medicalInfo.requestedMedication.drugName}</MedContextTitle>
              <MedContextSub>{`${medicalInfo.requestedMedication.dosage} · ${medicalInfo.requestedMedication.pillCount} ${dispenseUnit?.Name ? `${dispenseUnit?.Name}(s)` : 'units'}`} · {medicalInfo.requestedMedication.refills} refill{medicalInfo.requestedMedication.refills !== 1 ? 's' : ''}</MedContextSub>
            </MedContextCard>

            {
              medicalInfo.queuedPrescription && (
              <MedContextCard $haserror={isFlagged.toString()}>
                <MedContextLabel>Queued prescription</MedContextLabel>
                <MedContextTitle>{medicalInfo.queuedPrescription?.medication?.drugName}</MedContextTitle>
                <MedContextSub>{`${medicalInfo.queuedPrescription?.medication?.strength} · ${medicalInfo.requestedMedication.pillCount} ${dispenseUnit?.Name ? `${dispenseUnit?.Name.toLowerCase()}(s)` : 'units'}`} · {medicalInfo.requestedMedication.refills} refill{medicalInfo.requestedMedication.refills !== 1 ? 's' : ''}</MedContextSub>
                <MedContextNote>{medicalInfo.queuedPrescription?.directions}</MedContextNote>
              </MedContextCard>
            )}
          </MedContextGrid>

          <SnapshotCard>
            <SnapshotTitle>Clinical snapshot</SnapshotTitle>

            <SnapshotGrid>
              <SnapshotItem>
                <SnapshotLabel>Ht/Wt</SnapshotLabel>
                <SnapshotValue>{medicalInfo.heightWeight || 'N/A'}</SnapshotValue>
              </SnapshotItem>

              <SnapshotItem>
                <SnapshotLabel>BMI</SnapshotLabel>
                <SnapshotValue>{medicalInfo.bmi || 'N/A'}</SnapshotValue>
              </SnapshotItem>

              <SnapshotItem>
                <SnapshotLabel>BP</SnapshotLabel>
                <SnapshotValue>{medicalInfo.bloodPressure || 'N/A'}</SnapshotValue>
              </SnapshotItem>

              <SnapshotItem>
                <SnapshotLabel>Visit</SnapshotLabel>
                <SnapshotValue>{medicalInfo.visitType || 'N/A'}</SnapshotValue>
              </SnapshotItem>

              <SnapshotItem>
                <SnapshotLabel>Meds</SnapshotLabel>
                <SnapshotValue>{medicalInfo.medications || 'None'}</SnapshotValue>
              </SnapshotItem>

              <SnapshotItem>
                <SnapshotLabel>Allergies</SnapshotLabel>
                <SnapshotValue>{medicalInfo.allergies || 'None'}</SnapshotValue>
              </SnapshotItem>
            </SnapshotGrid>
          </SnapshotCard>

          <Footer>
            <FooterMeta>
              <VerifiedBadge>
                <svg viewBox="0 0 20 20" fill="currentColor">
                  <path
                    fillRule="evenodd"
                    d="M10 2l6 3v4c0 4.418-2.687 7.938-6 9-3.313-1.062-6-4.582-6-9V5l6-3z"
                    clipRule="evenodd"
                  />
                </svg>
                {patient.idVerified ? 'Identity verified' : 'ID not verified'}
              </VerifiedBadge>

              <StatusText>
                {eligibilityGuardTriggered ? 'Manual review required' : 'Cleared per protocol'}
              </StatusText>
            </FooterMeta>

            <Buttons>
              <SecondaryButton onClick={onViewCase} disabled={isSubmitting}>
                <EyeOutlined />
                Review full chart
              </SecondaryButton>
              {!isFlagged && (
                <PrimaryButton onClick={onPrescribe} disabled={isDisabled}>
                  {isSubmitting ? (
                    <>
                      <LoadingOutlined spin />
                      Prescribing...
                    </>
                  ) : (
                    <>
                      <MedicineBoxOutlined />
                      Approve & prescribe
                    </>
                  )}
                </PrimaryButton>
              )}
            </Buttons>
          </Footer>

          {isAIFlagged && !eligibilityGuardTriggered && (
            <FeedbackSection>
              <FeedbackLabel htmlFor="override-feedback">
                Provider feedback (optional)
              </FeedbackLabel>
              <FeedbackTextarea
                id="override-feedback"
                value={overrideFeedback}
                onChange={(e) => setOverrideFeedback(e.target.value)}
                placeholder="Provide any additional context or justification for prescribing this flagged case..."
                disabled={isSubmitting}
              />
              <OverridePrescribeButton
                onClick={() => onOverridePrescribe?.(overrideFeedback)}
                disabled={isOverrideDisabled}
              >
                {isSubmitting ? (
                  <>
                    <LoadingOutlined spin />
                    Prescribing...
                  </>
                ) : (
                  <>
                    <MedicineBoxOutlined />
                    Override & Prescribe
                  </>
                )}
              </OverridePrescribeButton>
            </FeedbackSection>
          )}
        </BodyInner>
      </Body>

      {hasError && (
        <ErrorMessage>
          <ExclamationCircleOutlined />
          <span>
            {eligibilityGuardTriggered 
              ? 'Case does not have proper templates configured. Please prescribe manually via the chart.' 
              : isAIFlagged
                ? 'This case has been flagged by the Clinical Eligibility Validator. Review the AI analysis above and provide override feedback to prescribe. Click "OK" to dismiss this case.'
                : 'Failed to prescribe medication. This case will need to be handled manually via the chart.'}
          </span>
          <ErrorDismissButton onClick={onErrorDismiss}>OK</ErrorDismissButton>
        </ErrorMessage>
      )}
    </Card>
  );
};

export default CaseCard;
